// DE profile › Overview › Memory (M7-05; 07 §Agent keys per DE): the DE's own Recall agent, from
// `GET digital-employees/{id}/recall-agent`. CompanyOS ensures the agent and rotates its `rk_`
// whenever the DE's CompanyOS agent key is issued or rotated; the key is held sealed and never
// shown. The panel says which state the agent is in, what it may read (bucket external ids, no
// personal memory), which CompanyOS key its `rk_` belongs to, and, for a DE hired from a personal
// crew, how the copy of the crew's chosen memory went. Managers with the `memory` add-on on only;
// one call.
import type { OperationOutput } from "@companyos/sdk";
import { Badge, Notice, SettingsSection, Skeleton } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { BrainIcon } from "lucide-react";
import type { Api } from "../../api/client";
import { formatDateTime } from "../format";
import { Facts } from "../screen-parts";
import { problemText } from "../settings/problem-text";

type AgentState = OperationOutput<"digitalEmployees.recallAgent">;

const STATE: Record<
  AgentState["state"],
  { label: string; tone: "success" | "warning" | "neutral"; text: string }
> = {
  not_configured: {
    label: "Not connected",
    tone: "neutral",
    text: "Recall is not connected to this deployment yet, so the DE has no memory agent.",
  },
  waiting: {
    label: "Waiting",
    tone: "warning",
    text: "The memory agent is made once the DE holds an active agent key (Keys tab).",
  },
  pending: {
    label: "Issuing",
    tone: "warning",
    text: "A Recall key is being issued; it is stored before it is used.",
  },
  held: {
    label: "Ready",
    tone: "success",
    text: "Runs read their context with the DE's own Recall key.",
  },
  retired: {
    label: "Retired",
    tone: "neutral",
    text: "The memory agent was retired with the DE. Its key no longer works.",
  },
};

const COPY_TEXT: Record<NonNullable<AgentState["copy"]>["state"], string> = {
  pending: "Waiting for Recall",
  done: "Copied",
  failed: "Failed",
};

export const recallAgentKey = (co: string, id: string) =>
  ["company", co, "digital-employees", id, "recall-agent"] as const;

/**
 * A bucket external id (`seat:…`, `team:…`) as words. A knowledge base's id is its space's id
 * (`{space id}` or `{space id}:kb{n}`, D14): "Knowledge". D8: the list is the agent's reach
 * (the company level and its own departments), as the last sync gave it.
 */
function grantLabel(g: string): string {
  const kind = g.split(":")[0] ?? g;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(kind)) return "Knowledge";
  const word: Record<string, string> = {
    company: "Company",
    seat: "Seat",
    team: "Team",
    space: "Space",
    agency: "Agency",
  };
  return word[kind] ?? kind;
}

export function RecallAgentSection({ api, co, deId }: { api: Api; co: string; deId: string }) {
  const q = useQuery({
    queryKey: recallAgentKey(co, deId),
    queryFn: () => api.client.digitalEmployees.recallAgent({ params: { co, id: deId } }),
  });
  const s = q.data;
  const meta = s ? STATE[s.state] : null;
  return (
    <SettingsSection
      icon={BrainIcon}
      title="Memory"
      description={meta?.text ?? "The DE's own Recall memory agent."}
      actions={
        meta ? (
          <Badge variant={meta.tone} data-recall-state={s?.state}>
            {meta.label}
          </Badge>
        ) : null
      }
    >
      {q.isPending ? (
        <div role="status" aria-label="Loading memory" className="flex flex-col gap-2">
          <Skeleton className="h-10" />
        </div>
      ) : q.isError ? (
        <Notice tone="danger" role="alert">
          {problemText(q.error, "The memory agent could not be loaded.")}
        </Notice>
      ) : s ? (
        <div className="flex flex-col gap-4" data-slot="recall-agent">
          <Facts
            items={[
              {
                label: "Reads",
                value: s.grants.length
                  ? [...new Set(s.grants.map(grantLabel))].join(", ") +
                    ` memory · ${s.grants.length} ${s.grants.length === 1 ? "space" : "spaces"}`
                  : "Nothing yet",
              },
              { label: "Personal memory", value: "None" },
              {
                label: "Key",
                value: s.key_for_prefix ? (
                  <span>
                    Issued for <code className="font-mono text-xs">{s.key_for_prefix}…</code>
                    {s.updated_at ? ` · ${formatDateTime(s.updated_at)}` : ""}
                  </span>
                ) : (
                  "Not issued"
                ),
              },
            ]}
          />
          {s.copy ? (
            <div className="flex flex-col gap-1 text-sm" data-slot="recall-copy">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Memory from the personal agent</span>
                <Badge
                  variant={
                    s.copy.state === "done"
                      ? "success"
                      : s.copy.state === "failed"
                        ? "danger"
                        : "warning"
                  }
                  data-copy-state={s.copy.state}
                >
                  {COPY_TEXT[s.copy.state]}
                </Badge>
              </div>
              <p className="text-muted-foreground">
                {s.copy.state === "done"
                  ? `${s.copy.created} new, ${s.copy.reinforced} already known, ${s.copy.proposed} for review, from ${s.copy.sources.length} ${s.copy.sources.length === 1 ? "group" : "groups"}. Each carries where it came from.`
                  : s.copy.state === "failed"
                    ? (s.copy.last_error ?? "The copy stopped after several tries.")
                    : `${s.copy.sources.length} ${s.copy.sources.length === 1 ? "group" : "groups"} will be copied once Recall has the DE's memory ready.`}
              </p>
            </div>
          ) : null}
          {s.hot_copy !== undefined ? <HotCopyLine hot={s.hot_copy} /> : null}
          {s.memory_queue ? (
            <MemoryQueueLine queue={s.memory_queue} hasCopy={Boolean(s.hot_copy)} />
          ) : null}
        </div>
      ) : null}
    </SettingsSection>
  );
}

const HELD_WORDS: Record<string, string> = {
  access_revoked: "it no longer reaches the space",
  space_gone: "the space was archived or replaced",
  writer_gone: "its memory key is gone",
  consent_withdrawn: "the consent no longer holds",
  refused: "Recall refused it",
  recall_error: "Recall kept failing on it",
};

/**
 * D14+D15: this agent's memories waiting for Recall, shown only when any wait or Recall is down
 * or holding the company. Counts and reasons only, never a memory's words.
 */
export function MemoryQueueLine({
  queue,
  hasCopy = false,
}: {
  queue: NonNullable<AgentState["memory_queue"]>;
  /** Whether this agent has a hot copy its runs fall back to (the setting on and a copy made). */
  hasCopy?: boolean;
}) {
  const held = Object.entries(queue.held_reasons)
    .map(([reason, n]) => `${n} ${HELD_WORDS[reason] ?? reason.replace("_", " ")}`)
    .join("; ");
  const parts = [
    queue.tenant_hold
      ? `Recall is holding this company's memory (${queue.tenant_hold === "tenant_archived" ? "archived" : "frozen"}); nothing is lost.`
      : queue.recall_down_since
        ? `Recall has not answered since ${formatDateTime(queue.recall_down_since)}${hasCopy ? "; runs use the hot copy" : ""}.`
        : null,
    queue.queued ? `${queue.queued} waiting to be sent when Recall answers.` : null,
    queue.held ? `${queue.held} held (${held}).` : null,
  ].filter(Boolean);
  return (
    <p className="text-sm text-muted-foreground" data-slot="memory-queue">
      {`Memory queue: ${parts.join(" ")}`}
    </p>
  );
}

/** D14 preview (only while the company's hot-copy setting is on): one read-only status line. */
function HotCopyLine({ hot }: { hot: AgentState["hot_copy"] }) {
  return (
    <p className="text-sm text-muted-foreground" data-slot="hot-copy">
      {hot
        ? `Hot copy: ${Math.max(1, Math.round(hot.size_bytes / 1024))} KB, refreshed ${formatDateTime(hot.fetched_at)}, from ${hot.source_spaces.join(", ") || "no spaces"}${hot.trimmed.length ? ` (left out to fit: ${hot.trimmed.map((k) => k.replace("_", " ")).join(", ")})` : ""}.`
        : "Hot copy: none yet (made in the next few minutes while Recall answers)."}
    </p>
  );
}
